<template>
  <div class="hub-dashboard">
    <!-- Welcome Header Banner -->
    <div class="hub-banner glass-card pa-6 pa-md-8 mb-8">
      <v-row align="center">
        <v-col cols="12" md="8">
          <div class="d-flex align-center ga-2 mb-2">
            <v-chip size="small" color="primary" variant="flat" class="font-weight-bold">
              MEDIA VAULT
            </v-chip>
            <span class="text-caption text-medium-emphasis">Unified Library</span>
          </div>
          <h1 class="text-h4 text-md-h3 font-weight-bold mb-2">
            Welcome back, <span class="text-gradient-primary">{{ userName }}</span>!
          </h1>
          <p class="text-body-1 text-medium-emphasis mb-0">
            Select a collection to explore your categorized library, manage wishlists, or add new titles.
          </p>
        </v-col>
        <v-col cols="12" md="4" class="text-md-right d-flex justify-start justify-md-end ga-2">
          <v-btn
            to="/movies/add-post"
            color="primary"
            class="btn-modern btn-glow-primary"
            prepend-icon="mdi-plus"
          >
            Quick Add
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Bento Grid Collections -->
    <v-row class="hub-grid" dense>
      <v-col
        v-for="item in collections"
        :key="item.id"
        cols="12"
        sm="6"
        lg="3"
      >
        <v-card
          class="bento-collection-card h-100 d-flex flex-column"
          :class="`bento-collection-card--${item.id}`"
        >
          <!-- Card Header / Glow Accent -->
          <div class="bento-card-header pa-5 pb-3">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="bento-icon-wrap" :style="{ background: item.iconBg }">
                <v-icon :color="item.color" size="32">{{ item.icon }}</v-icon>
              </div>
              <v-chip
                size="x-small"
                :color="item.color"
                variant="tonal"
                class="font-weight-bold"
              >
                {{ item.badge }}
              </v-chip>
            </div>
            <h2 class="text-h5 font-weight-bold mb-1">{{ item.title }}</h2>
            <p class="text-body-2 text-medium-emphasis bento-desc">{{ item.description }}</p>
          </div>

          <v-spacer></v-spacer>

          <!-- Quick Action Buttons -->
          <div class="pa-5 pt-0">
            <v-divider class="mb-4"></v-divider>
            <div class="d-flex flex-column ga-2">
              <v-btn
                :to="item.to"
                :color="item.color"
                variant="flat"
                block
                class="btn-modern font-weight-bold text-white"
                prepend-icon="mdi-view-grid"
              >
                Browse {{ item.title }}
              </v-btn>

              <div class="d-flex ga-2">
                <v-btn
                  :to="item.wishlistTo"
                  variant="tonal"
                  size="small"
                  class="flex-grow-1 font-weight-medium"
                  prepend-icon="mdi-bookmark-outline"
                >
                  Wishlist
                </v-btn>
                <v-btn
                  :to="item.addTo"
                  variant="tonal"
                  size="small"
                  class="flex-grow-1 font-weight-medium"
                  prepend-icon="mdi-plus"
                >
                  Add New
                </v-btn>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'CollectionHub',
  setup() {
    const store = useStore()
    const user = computed(() => store.getters['auth/user'] || {})

    const userName = computed(() => {
      if (user.value.firstname) return user.value.firstname
      if (user.value.name) return user.value.name.split(' ')[0]
      return 'Collector'
    })

    const collections = [
      {
        id: 'movies',
        title: 'Movies',
        description: 'Personal cinema vault with categories, ratings, cast photos, and YouTube trailers.',
        icon: 'mdi-movie-open',
        color: '#6366f1',
        iconBg: 'rgba(99, 102, 241, 0.15)',
        badge: 'CINEMA',
        to: '/movies/homeview',
        wishlistTo: '/movies/wishlist',
        addTo: '/movies/add-post',
      },
      {
        id: 'books',
        title: 'Books',
        description: 'Organize your reading list, published editions, and bookshelf categories.',
        icon: 'mdi-book-open-page-variant',
        color: '#10b981',
        iconBg: 'rgba(16, 185, 129, 0.15)',
        badge: 'READING',
        to: '/books/homeview',
        wishlistTo: '/books/wishlist',
        addTo: '/books/add-post',
      },
      {
        id: 'audiobooks',
        title: 'AudioBooks',
        description: 'Audio library cataloging narrators, listening durations, and quality tags.',
        icon: 'mdi-headphones',
        color: '#f43f5e',
        iconBg: 'rgba(244, 63, 94, 0.15)',
        badge: 'AUDIO',
        to: '/audiobooks/homeview',
        wishlistTo: '/audiobooks/wishlist',
        addTo: '/audiobooks/add-post',
      },
      {
        id: 'comics',
        title: 'Comics & Manga',
        description: 'Manage comic series, graphic novels, tome volumes, and illustrators.',
        icon: 'mdi-draw',
        color: '#f59e0b',
        iconBg: 'rgba(245, 158, 11, 0.15)',
        badge: 'COMICS',
        to: '/comics/homeview',
        wishlistTo: '/comics/wishlist',
        addTo: '/comics/add-post',
      },
    ]

    return {
      userName,
      collections,
    }
  },
}
</script>

<style scoped>
.hub-dashboard {
  max-width: 1360px;
  margin: 0 auto;
}

.hub-banner {
  position: relative;
  overflow: hidden;
  border-radius: 24px !important;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(236, 72, 153, 0.05) 100%) !important;
}

.bento-collection-card {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(14px);
  border: 1px solid rgba(128, 128, 128, 0.12) !important;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.3s ease;
}

.v-theme--dark .bento-collection-card {
  background: rgba(18, 24, 38, 0.75) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.bento-collection-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.2);
}

.bento-collection-card--movies:hover {
  border-color: rgba(99, 102, 241, 0.45) !important;
  box-shadow: 0 16px 32px rgba(99, 102, 241, 0.25);
}

.bento-collection-card--books:hover {
  border-color: rgba(16, 185, 129, 0.45) !important;
  box-shadow: 0 16px 32px rgba(16, 185, 129, 0.25);
}

.bento-collection-card--audiobooks:hover {
  border-color: rgba(244, 63, 94, 0.45) !important;
  box-shadow: 0 16px 32px rgba(244, 63, 94, 0.25);
}

.bento-collection-card--comics:hover {
  border-color: rgba(245, 158, 11, 0.45) !important;
  box-shadow: 0 16px 32px rgba(245, 158, 11, 0.25);
}

.bento-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 16px;
}

.bento-desc {
  min-height: 2.8em;
  line-height: 1.4;
}
</style>

